import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { TixxSelectTranslationsService } from '~shared/translations';
import { ModalService } from '~shared/components/elements/global-modal/modal.service';
import { NotificationType } from '~core/enum';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { AttributeService, DefaultNotificationService, TixxSelectSubmissionService } from '~core/services';
import { Club, Lottery, LotteryExecutionResults, LotteryExecutionSummary } from '~core/models';

/**
 * Component that displays the lottery settings step of the execution process
 */
@UntilDestroy()
@Component({
  selector: 'app-lottery-settings',
  templateUrl: './lottery-settings.component.html',
  styleUrls: ['../../../lottery-execution.component.scss']
})

export class LotterySettingsComponent implements OnInit {
  @Input() public selectedLottery: Lottery;
  @Input() public selectedClub: Club;
  @Input() public savedFilteredForm: any;
  @Input() public savedDeviationForm: any;
  @Input() public iterationSubmissionFilters: any;
  @Input() public executionSummary: LotteryExecutionSummary;
  @Output() public nextStepEmitter: EventEmitter<any> = new EventEmitter(null);
  @Output() public backStepEmitter: EventEmitter<any> = new EventEmitter(null);

  public executionResults: LotteryExecutionResults;
  public spinnerName: string = 'summaryLoading';
  public lotteryExecutionForm: UntypedFormGroup;
  public i18nExecuteTitle: string;
  public i18nExecuteParagraph: string;
  public i18nExecutionActive: string;
  public i18nNoSubmissionsDrawn: string;

  constructor(
    protected attributeService: AttributeService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    private submissionService: TixxSelectSubmissionService,
    private notificationService: DefaultNotificationService,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: SpinnerService,
    private modalService: ModalService,
    private tixxSelectTranslationService: TixxSelectTranslationsService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.fillLotteryForm();
    this.i18nExecuteTitle = $localize`:@@tixxSelect.modalTitle.executeLottery:Execute Lottery?`;
    this.i18nExecuteParagraph = $localize`:@@tixxSelect.modalParagraph.executeLottery:The following process will execute the lottery based on the selected filter criteria and start the automated seating process via Tixx. This process cannot be revoked. Would you like to proceed?`;
    this.i18nExecutionActive = $localize`:@@lotteryExecution.alertError.executionActive:Execution already has an active iteration for this price category.`;
    this.i18nNoSubmissionsDrawn = $localize`:@@lotteryExecution.alertError.noSubmissionsDrawn:Iteration could not be created because no submissions were drawn for the defined filters.`;
  }

  /**
   * Create the lottery form.
   */
  public fillLotteryForm(): void {
    this.lotteryExecutionForm = this.formBuilder.group({
      capacityLimit: [this.limitCapacity, [Validators.required, Validators.min(1), Validators.max(this.limitCapacity)]],
      lottery: [this.selectedLottery['@id']],
      iterationSubmissionFilters: [this.iterationSubmissionFilters],
    });
  }

  get limitCapacity(): number {
    const seats = this.executionSummary.priceCategoryCapacity;
    const tickets = this.executionSummary.numberOfRequestedTickets;
    return tickets > seats ? seats : tickets;
  }

  public formSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      this.submissionService.postExecutionIterations(form.value)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.createReservations(res.id);
          },
          error: ((error: HttpErrorResponse) => {
            this.modalService.setStatus(false);
            if (error.error['hydra:description'] === 'NO_DRAWN_SUBMISSIONS') {
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: this.i18nNoSubmissionsDrawn
              });
            } else if (error.status === 400) {
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: this.i18nExecutionActive
              });
            }
          })
        });
    }
  }

  public async showExecuteModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nExecuteTitle, this.i18nExecuteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.formSubmit(this.lotteryExecutionForm);
    }
  }

  public createReservations(id: any): void {
    this.spinnerService.show(this.spinnerName);
    const data = { iterationId: id };

    this.submissionService.postIterationReservation(data).pipe(untilDestroyed(this))
      .subscribe(res => {
        this.modalService.setStatus(false);
        this.notificationService.showNotification({
          type: NotificationType.SUCCESS,
          message: this.tixxSelectTranslationService.i18nLotterySeatingInitiated
        });
        this.router.navigate(['../'], { relativeTo: this.route });
      });
  }
}
